import React, { Component } from "react";

class FortniteAmbientBackground extends Component {
    constructor(props) {
        super(props);
        this.canvasRef = React.createRef();
        this.animationFrame = null;
        this.resizeTimeout = null;
        this.particles = [];
        this.lights = [];
        this.lastTime = 0;
    }

    componentDidMount() {
        this.ctx = this.canvasRef.current.getContext("2d");
        this.setupScene();
        window.addEventListener("resize", this.handleResize, { passive: true });
        this.animationFrame = window.requestAnimationFrame(this.animate);
    }

    componentWillUnmount() {
        if (this.animationFrame) {
            window.cancelAnimationFrame(this.animationFrame);
        }
        if (this.resizeTimeout) {
            window.clearTimeout(this.resizeTimeout);
        }
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        if (this.resizeTimeout) {
            window.clearTimeout(this.resizeTimeout);
        }
        this.resizeTimeout = window.setTimeout(() => {
            this.setupScene();
        }, 120);
    };

    range = (min, max) => min + (max - min) * Math.random();

    weightedRange = (min, max, weightedStart, weightedEnd, weightStrength) => {
        if (Math.random() <= weightStrength) {
            return this.range(weightedStart, weightedEnd);
        }
        return this.range(min, max);
    };

    setupScene = () => {
        const canvas = this.canvasRef.current;
        if (!canvas || !this.ctx) {
            return;
        }

        const rect = canvas.getBoundingClientRect();
        this.width = Math.max(1, Math.floor(rect.width));
        this.height = Math.max(1, Math.floor(rect.height));
        canvas.width = this.width;
        canvas.height = this.height;

        this.lights = [
            { color: "#6ac6e8", alpha: 0.6, rx: 20, ry: 10, ox: 0, oy: 0, speed: 0.28 },
            { color: "#54d5e8", alpha: 0.28, rx: 80, ry: 40, ox: -80, oy: 0, speed: 0.21 },
            { color: "#2ae8d8", alpha: 0.16, rx: 10, ry: 40, ox: 110, oy: -40, speed: 0.33 }
        ];

        const settings = [
            { id: "small", count: 220, radius: 2.5, alphaMax: 0.35, areaHeight: 0.5, color: "#0cdbf3", fill: false },
            { id: "medium", count: 90, radius: 7, alphaMax: 0.28, areaHeight: 1, color: "#6fd2f3", fill: true },
            { id: "large", count: 12, radius: 24, alphaMax: 0.16, areaHeight: 1, color: "#93e9f3", fill: true }
        ];

        this.particles = [];
        settings.forEach((group) => {
            for (let i = 0; i < group.count; i += 1) {
                this.particles.push(this.createParticle(group));
            }
        });
    };

    createParticle = (group) => {
        const verticalCenterStart = this.height * (2 - group.areaHeight / 2) / 4;
        const verticalCenterEnd = this.height * (2 + group.areaHeight / 2) / 4;
        const initX = this.weightedRange(0, this.width, this.width * 0.25, this.width * 0.75, 0.6);
        const initY = this.weightedRange(0, this.height, verticalCenterStart, verticalCenterEnd, 0.8);
        const distance = group.radius * 2;

        return {
            ...group,
            x: initX,
            y: initY,
            initX,
            initY,
            distance,
            alpha: this.range(0, 0.1),
            scale: this.range(0.3, 1),
            speed: this.range(0.6, 2.2),
            tx: this.range(initX - distance, initX + distance),
            ty: this.range(initY - distance, initY + distance),
            tScale: this.range(0.3, 1),
            alphaDir: Math.random() > 0.5 ? 1 : -1
        };
    };

    animate = (time) => {
        if (!this.ctx) {
            return;
        }

        const dt = this.lastTime ? Math.min(33, time - this.lastTime) : 16;
        this.lastTime = time;
        const step = dt / 16.66;

        this.drawBackground(time);
        this.drawLights(time);
        this.updateAndDrawParticles(step);

        this.animationFrame = window.requestAnimationFrame(this.animate);
    };

    drawBackground = () => {
        const gradient = this.ctx.createLinearGradient(0, 0, this.width, this.height);
        gradient.addColorStop(0.5, "#191d1e");
        gradient.addColorStop(1, "#283139");
        this.ctx.globalCompositeOperation = "source-over";
        this.ctx.fillStyle = gradient;
        this.ctx.fillRect(0, 0, this.width, this.height);
    };

    drawLights = (time) => {
        const cx = this.width / 2;
        const cy = this.height / 2;

        this.lights.forEach((light, index) => {
            const t = time * 0.001 * light.speed;
            const x = cx + light.ox + Math.sin(t + index) * (32 + index * 14);
            const y = cy + light.oy + Math.cos(t + index * 0.7) * (20 + index * 10);

            this.ctx.save();
            this.ctx.globalCompositeOperation = "screen";
            this.ctx.globalAlpha = light.alpha;
            this.ctx.fillStyle = light.color;
            this.ctx.shadowColor = light.color;
            this.ctx.shadowBlur = 80;
            this.ctx.beginPath();
            this.ctx.ellipse(x, y, light.rx, light.ry, 0, 0, Math.PI * 2);
            this.ctx.fill();
            this.ctx.restore();
        });
    };

    updateAndDrawParticles = (step) => {
        this.ctx.globalCompositeOperation = "lighter";

        this.particles.forEach((particle) => {
            const ease = 0.012 * particle.speed * step;
            particle.x += (particle.tx - particle.x) * ease;
            particle.y += (particle.ty - particle.y) * ease;
            particle.scale += (particle.tScale - particle.scale) * ease;

            if (Math.abs(particle.tx - particle.x) < 0.9 && Math.abs(particle.ty - particle.y) < 0.9) {
                particle.tx = this.range(particle.initX - particle.distance, particle.initX + particle.distance);
                particle.ty = this.range(particle.initY - particle.distance, particle.initY + particle.distance);
                particle.tScale = this.range(0.3, 1);
                particle.speed = this.range(0.6, 2.2);
            }

            particle.alpha += particle.alphaDir * 0.004 * step;
            if (particle.alpha >= particle.alphaMax) {
                particle.alpha = particle.alphaMax;
                particle.alphaDir = -1;
            }
            if (particle.alpha <= 0.02) {
                particle.alpha = 0.02;
                particle.alphaDir = 1;
            }

            this.ctx.save();
            this.ctx.globalAlpha = particle.alpha;
            this.ctx.strokeStyle = particle.color;
            this.ctx.fillStyle = particle.color;
            this.ctx.shadowColor = particle.color;
            this.ctx.shadowBlur = particle.fill ? particle.radius : 0;
            this.ctx.beginPath();
            this.ctx.arc(particle.x, particle.y, particle.radius * particle.scale, 0, Math.PI * 2);
            if (particle.fill) {
                this.ctx.fill();
            } else {
                this.ctx.lineWidth = 1;
                this.ctx.stroke();
            }
            this.ctx.restore();
        });
    };

    render() {
        return <canvas className="fortnite-ambient-canvas" ref={this.canvasRef} aria-hidden="true" />;
    }
}

export default FortniteAmbientBackground;
